import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "@/lib/posthog-client";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type maplibregl from "maplibre-gl";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  Input,
} from "@trotroways/ui";
import { Map, MapMarker, MapRoute, MarkerContent } from "@/components/map-view";
import { Search } from "lucide-react";
import { countryToFlag } from "@trotroways/api";
import { BottomSheet } from "@/components/bottom-sheet";
import { StationPin } from "@/components/station-pin";
import { EventPin } from "@/components/event-pin";
import { EventDetailPanel } from "@/components/event-detail-panel";
import { CityBadge } from "@/components/map-ui/city-badge";
import { MapControls } from "@/components/map-controls";
import { RouteDetailsPanel } from "@/components/route-details-panel";
import { RouteOptionsPanel } from "@/components/route-options-panel";
import { SearchCard, type SearchMode } from "@/components/search-card";
import { StopInfoPanel } from "@/components/stop-info-panel";
import { useLocationSearch } from "@/hooks/use-location-search";
import { type MapStyleName, useMap, resolveMapStyle } from "@/hooks/use-map";
import {
  api,
  type Event,
  type EventType,
  type Location,
  type PublicCity,
  type RouteOption,
  type SearchResult,
  type Station,
  type StationDetails,
} from "@/lib/api";
import { languages } from "@/lib/constants";
import {
  isRouteSaved,
  listRecentSearches,
  recordRecentSearch,
  removeSavedRoute,
  saveRoute,
  type SavedRoute,
} from "@/lib/saved-routes";
import { getCoverageWarning } from "@/lib/coverage";

type SearchParams = {
  search_mode?: SearchMode;
  from_lat?: number;
  from_lon?: number;
  from_name?: string;
  from_address?: string;
  to_lat?: number;
  to_lon?: number;
  to_name?: string;
  to_address?: string;
};

const ACCRA_CENTER: [number, number] = [-0.187, 5.6037];
const DEFAULT_CITY_SLUG = "accra";

type CardView = "search" | "options" | "details" | "event-detail" | "station-detail";

export const Route = createFileRoute("/")({
  component: Home,
  // Prefetch cities on the server so the city badge (LCP element) is
  // rendered in the server HTML instead of after client-side hydration.
  loader: async ({ context }) => {
    await context.queryClient
      .ensureQueryData({
        queryKey: ["cities"],
        queryFn: api.getCities,
        staleTime: 5 * 60 * 1000,
      })
      .catch(() => undefined);
  },
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    search_mode: search.search_mode === "route" ? "route" : undefined,
    from_lat:
      typeof search.from_lat === "number"
        ? search.from_lat
        : typeof search.from_lat === "string"
          ? Number(search.from_lat)
          : undefined,
    from_lon:
      typeof search.from_lon === "number"
        ? search.from_lon
        : typeof search.from_lon === "string"
          ? Number(search.from_lon)
          : undefined,
    from_name: typeof search.from_name === "string" ? search.from_name : undefined,
    from_address:
      typeof search.from_address === "string" ? search.from_address : undefined,
    to_lat:
      typeof search.to_lat === "number"
        ? search.to_lat
        : typeof search.to_lat === "string"
          ? Number(search.to_lat)
          : undefined,
    to_lon:
      typeof search.to_lon === "number"
        ? search.to_lon
        : typeof search.to_lon === "string"
          ? Number(search.to_lon)
          : undefined,
    to_name: typeof search.to_name === "string" ? search.to_name : undefined,
    to_address: typeof search.to_address === "string" ? search.to_address : undefined,
  }),
});




function Home() {
  const posthog = usePostHog();
  const {
    from_lat,
    from_lon,
    from_name,
    from_address,
    to_lat,
    to_lon,
    to_name,
    to_address,
  } = useSearch({ from: "/" });
  const navigate = useNavigate({ from: "/" });

  const [cardView, setCardView] = useState<CardView>("search");
  const [searchMode, setSearchMode] = useState<SearchMode>("route");
  const [fromLocation, setFromLocation] = useState<Location | null>(() => {
    if (from_lat !== undefined && from_lon !== undefined) {
      return { latitude: from_lat, longitude: from_lon, name: from_name ?? "Origin", address: from_address };
    }
    return null;
  });
  const [toLocation, setToLocation] = useState<Location | null>(() => {
    if (to_lat !== undefined && to_lon !== undefined) {
      return { latitude: to_lat, longitude: to_lon, name: to_name ?? "Destination", address: to_address };
    }
    return null;
  });
  const [fromInput, setFromInput] = useState(from_name ?? "");
  const [toInput, setToInput] = useState(to_name ?? "");
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const [mapStyle, setMapStyle] = useState<MapStyleName>("standard");
  const [cities, setCities] = useState<PublicCity[]>([]);
  const [selectedCitySlug, setSelectedCitySlug] = useState(DEFAULT_CITY_SLUG);
  const [locationDenied, setLocationDenied] = useState(false);
  const [isFromGps, setIsFromGps] = useState(false);
  const fromLocationSetByUser = useRef(false);
  const [languageSearch, setLanguageSearch] = useState("");
  const [routes, setRoutes] = useState<RouteOption[]>([]);
  const [routeSearchError, setRouteSearchError] = useState<string | null>(null);
  const [recentSearches, setRecentSearches] = useState<SavedRoute[]>([]);
  const [savedRoutesVersion, setSavedRoutesVersion] = useState(0);
  const [selectedRoute, setSelectedRoute] = useState<RouteOption | null>(null);
  const [isLoadingRoutes, setIsLoadingRoutes] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [selectedStationDetails, setSelectedStationDetails] = useState<StationDetails | null>(null);
  const [isLoadingStationDetails, setIsLoadingStationDetails] = useState(false);
  const stationRequestRef = useRef(0);
  const eventsVisible = true;
  const eventTypeFilter: EventType | null = null;
  const [previousCardView, setPreviousCardView] = useState<CardView>("search");
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null);
  const [sheetState, setSheetState] = useState<"peek" | "expanded">("peek");

  const {
    results: fromSearchResults,
    setResults: setFromSearchResults,
    isSearching: isSearchingFrom,
  } = useLocationSearch(fromInput);

  const {
    results: toSearchResults,
    setResults: setToSearchResults,
    isSearching: isSearchingTo,
  } = useLocationSearch(toInput);

  useEffect(() => {
    setRecentSearches(listRecentSearches());
  }, []);

  const handleMapReady = useCallback((center: Location) => {
    if (!fromLocationSetByUser.current) {
      setFromLocation(center);
    }
  }, []);

  const handleOriginChange = useCallback(async (location: Location) => {
    const locationName = await api.reverseGeocode(
      location.latitude,
      location.longitude,
    );
    setIsFromGps(false);
    setFromInput(locationName);
    setFromLocation({
      ...location,
      name: locationName,
    });
  }, []);

  const handleDestinationChange = useCallback(async (location: Location) => {
    const locationName = await api.reverseGeocode(
      location.latitude,
      location.longitude,
    );
    setToInput(locationName);
    setToLocation({
      ...location,
      name: locationName,
    });
  }, []);

  const {
    setDestination,
    setOrigin,
    renderRoute,
    refitRoute,
    loadStations,
    setSelectedStation: setSelectedStationPin,
    flyTo,
    zoomIn,
    zoomOut,
    loadEvents,
    selectEvent,
    flyToEvent,
    events,
    selectedEventId,
    stations,
    selectedStationId,
    stationVisibility,
    viewportBounds,
    compactMode,
  } = useMap({
    externalMap: mapInstance,
    onOriginChange: handleOriginChange,
    onDestinationChange: handleDestinationChange,
    onMapReady: handleMapReady,
    onMapClick: () => {
      setSelectedStation(null);
      setSelectedStationDetails(null);
      setSelectedStationPin(null);
      setCardView((prev) => (prev === "station-detail" ? previousCardView : prev));
    },
  });

  // Re-fit the selected route when the sheet expands/collapses so it stays
  // fully visible above the sheet.
  useEffect(() => {
    if (!selectedRoute) return;
    const t = setTimeout(() => refitRoute(), 350);
    return () => clearTimeout(t);
  }, [sheetState, selectedRoute, refitRoute]);

  const selectedCity = cities.find((c) => c.slug === selectedCitySlug) ?? null;
  const coverageWarning = useMemo(
    () => getCoverageWarning(fromLocation, toLocation, stations, selectedCity),
    [fromLocation, toLocation, stations, selectedCity],
  );
  const routeSaved = useMemo(() => {
    void savedRoutesVersion;
    return fromLocation && toLocation ? isRouteSaved(fromLocation, toLocation) : false;
  }, [fromLocation, toLocation, savedRoutesVersion]);

  const handleEventMarkerClick = useCallback((event: Event) => {
    posthog.capture("event_detail_viewed", {
      event_id: event.id,
      event_title: event.title,
      event_type: event.event_type,
      source: "map_pin",
    });
    setSelectedStation(null);
    setSelectedStationDetails(null);
    setSelectedStationPin(null);
    setSelectedEvent(event);
    setPreviousCardView((prev) =>
      prev !== "event-detail" ? prev : "search",
    );
    setCardView("event-detail");
  }, [posthog, setSelectedStationPin]);

  const handleStationMarkerClick = useCallback(async (station: Station) => {
    posthog.capture("station_panel_viewed", {
      station_id: station.id,
      station_name: station.name,
    });

    stationRequestRef.current += 1;
    const requestId = stationRequestRef.current;

    setSelectedStation(station);
    setSelectedStationDetails(null);
    setIsLoadingStationDetails(true);
    setSelectedStationPin(station.id);
    setPreviousCardView((prev) =>
      prev !== "station-detail" ? prev : "search",
    );
    setCardView("station-detail");

    const details = await api.getStationDetails(station.id);
    if (requestId !== stationRequestRef.current) {
      return;
    }

    setSelectedStationDetails(details);
    setIsLoadingStationDetails(false);
  }, [posthog, api, setSelectedStationPin]);

  const visibleStations = useMemo(() => {
    return stations.filter((station) => {
      if (station.id === selectedStationId) return true;
      const vis = stationVisibility.get(station.id);
      if (!vis?.visible) return false;
      if (viewportBounds && !viewportBounds.contains([station.lon, station.lat])) return false;
      return true;
    });
  }, [stations, stationVisibility, viewportBounds, selectedStationId]);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        posthog.capture("location_permission_granted");
        const loc: Location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          name: "Your Location",
        };
        const locationName = await api.reverseGeocode(
          loc.latitude,
          loc.longitude,
        );
        loc.name = locationName;
        fromLocationSetByUser.current = true;
        setFromLocation(loc);
        setFromInput("Current location");
        setIsFromGps(true);
        setLocationDenied(false);
        setOrigin(loc);
        flyTo(loc);
        posthog.capture("current_location_used");
      },
      () => {
        posthog.capture("location_permission_denied");
        setLocationDenied(true);
      },
    );
  }, [flyTo, setOrigin, posthog]);

  const handleRecenter = useCallback(() => {
    if (fromLocation) {
      flyTo(fromLocation);
    } else {
      requestLocation();
    }
  }, [fromLocation, flyTo, requestLocation]);

  const handleExitGpsMode = useCallback(() => {
    setIsFromGps(false);
  }, []);

  useEffect(() => {
    if (from_lat !== undefined && from_lon !== undefined) {
      const location: Location = {
        latitude: from_lat,
        longitude: from_lon,
        name: from_name || "Origin",
        address: from_address,
      };
      fromLocationSetByUser.current = true;
      setFromLocation(location);
      setIsFromGps(false);
      setFromInput(location.name || "Origin");
      setOrigin(location);
    }
  }, [from_lat, from_lon, from_name, from_address, setOrigin]);

  useEffect(() => {
    if (to_lat !== undefined && to_lon !== undefined) {
      const location: Location = {
        latitude: to_lat,
        longitude: to_lon,
        name: to_name || "Destination",
        address: to_address,
      };
      setToLocation(location);
      setToInput(to_name || "Destination");
      setDestination(location);
    }
  }, [to_lat, to_lon, to_name, to_address, setDestination]);

  useEffect(() => {
    navigate({
      replace: true,
      search: (prev) => ({
        ...prev,
        search_mode: searchMode,
        from_lat: fromLocation?.latitude,
        from_lon: fromLocation?.longitude,
        from_name: fromLocation?.name,
        from_address: fromLocation?.address,
        to_lat: toLocation?.latitude,
        to_lon: toLocation?.longitude,
        to_name: toLocation?.name,
        to_address: toLocation?.address,
      }),
    });
  }, [
    navigate,
    searchMode,
    fromLocation?.latitude,
    fromLocation?.longitude,
    fromLocation?.name,
    fromLocation?.address,
    toLocation?.latitude,
    toLocation?.longitude,
    toLocation?.name,
    toLocation?.address,
  ]);

  useEffect(() => {
    const fetchStations = async () => {
      const stations = await api.getStations(selectedCitySlug);
      loadStations(stations);
    };
    fetchStations();
  }, [loadStations, selectedCitySlug]);

  const { data: citiesData } = useQuery({
    queryKey: ["cities"],
    queryFn: api.getCities,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    if (!citiesData) return;
    setCities(citiesData);

    if (citiesData.length === 0) return;

    const hasSelected = citiesData.some((city) => city.slug === selectedCitySlug);
    if (!hasSelected) {
      const defaultCity = citiesData.find((city) => city.slug === DEFAULT_CITY_SLUG) ?? citiesData[0];
      setSelectedCitySlug(defaultCity.slug);
    }
  }, [citiesData, selectedCitySlug]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await api.getEvents({
          eventType: eventTypeFilter || undefined,
          limit: 100,
        });
        loadEvents(response.data);
      } catch (error) {
        console.error("Failed to fetch events:", error);
      }
    };
    if (eventsVisible) {
      fetchEvents();
    }
  }, [loadEvents, eventTypeFilter, eventsVisible]);

  useEffect(() => {
    if (selectedEvent) {
      selectEvent(selectedEvent.id);
      flyToEvent(selectedEvent);
    } else {
      selectEvent(null);
    }
  }, [selectedEvent, selectEvent, flyToEvent]);

  const handleFromSelect = useCallback(
    (result: SearchResult) => {
      const location = {
        latitude: result.latitude,
        longitude: result.longitude,
        name: result.name,
        address: result.address,
      };
      fromLocationSetByUser.current = true;
      setFromInput(result.name);
      setIsFromGps(false);
      setFromLocation(location);
      setFromSearchResults([]);
      setOrigin(location);
      flyTo(location);
      posthog.capture("location_selected", {
        field: "origin",
        location_name: result.name,
      });
    },
    [flyTo, setOrigin, posthog],
  );

  const handleToSelect = useCallback(
    (result: SearchResult) => {
      const location = {
        latitude: result.latitude,
        longitude: result.longitude,
        name: result.name,
        address: result.address,
      };
      setToInput(result.name);
      setToLocation(location);
      setToSearchResults([]);
      setDestination(location);
      posthog.capture("location_selected", {
        field: "destination",
        location_name: result.name,
      });
    },
    [setDestination, posthog],
  );

  const runSearch = useCallback(
    async (origin: Location, dest: Location) => {
      posthog.capture("route_search_started", {
        origin: origin.name,
        destination: dest.name,
        search_mode: searchMode,
        city_slug: selectedCitySlug,
        origin_is_gps: isFromGps,
      });
      setRouteSearchError(null);
      setIsLoadingRoutes(true);
      setCardView("options");

      try {
        const routeOptions = await api.planRoute(origin, dest, {
          citySlug: selectedCitySlug,
        });
        setRoutes(routeOptions);
        recordRecentSearch(origin, dest);
        setRecentSearches(listRecentSearches());
        if (routeOptions.length === 0) {
          posthog.capture("search_results_empty", {
            origin: origin.name,
            destination: dest.name,
            city_slug: selectedCitySlug,
            origin_is_gps: isFromGps,
          });
        }
        posthog.capture("route_options_viewed", {
          origin: origin.name,
          destination: dest.name,
          route_count: routeOptions.length,
        });
        if (routeOptions.length > 0) {
          renderRoute(routeOptions[0]);
        }
      } catch (error) {
        console.error("Failed to find routes:", error);
        const message =
          error instanceof Error ? error.message : "Unknown error";
        setRouteSearchError(message);
        posthog.capture("search_failed", {
          origin: origin.name,
          destination: dest.name,
          city_slug: selectedCitySlug,
          error_message: message.slice(0, 200),
        });
      } finally {
        setIsLoadingRoutes(false);
      }
    },
    [renderRoute, posthog, selectedCitySlug, searchMode, isFromGps],
  );

  const handleFindRoutes = useCallback(async () => {
    if (!fromLocation || !toLocation) return;
    await runSearch(fromLocation, toLocation);
  }, [fromLocation, toLocation, runSearch]);

  const handleSelectRecentSearch = useCallback(
    (recent: SavedRoute) => {
      const origin: Location = {
        latitude: recent.fromLat,
        longitude: recent.fromLon,
        name: recent.fromName,
      };
      const dest: Location = {
        latitude: recent.toLat,
        longitude: recent.toLon,
        name: recent.toName,
      };
      fromLocationSetByUser.current = true;
      setFromInput(recent.fromName);
      setToInput(recent.toName);
      setFromLocation(origin);
      setToLocation(dest);
      setOrigin(origin);
      setDestination(dest);
      posthog.capture("recent_search_selected", {
        origin: recent.fromName,
        destination: recent.toName,
      });
      void runSearch(origin, dest);
    },
    [posthog, runSearch, setOrigin, setDestination],
  );

  const handleToggleSaveRoute = useCallback(() => {
    if (!fromLocation || !toLocation) return;
    if (isRouteSaved(fromLocation, toLocation)) {
      removeSavedRoute(fromLocation, toLocation);
      posthog.capture("route_unsaved", {
        origin: fromLocation.name,
        destination: toLocation.name,
      });
    } else {
      saveRoute(fromLocation, toLocation);
      posthog.capture("route_saved", {
        origin: fromLocation.name,
        destination: toLocation.name,
        city_slug: selectedCitySlug,
      });
    }
    setSavedRoutesVersion((v) => v + 1);
  }, [fromLocation, toLocation, posthog, selectedCitySlug]);


  const handleReportMissingRoute = useCallback(() => {
    posthog.capture("missing_route_reported", {
      origin: fromLocation?.name,
      destination: toLocation?.name,
      city_slug: selectedCitySlug,
    });
  }, [fromLocation?.name, toLocation?.name, posthog, selectedCitySlug]);

  const handleRouteOptionSelect = useCallback(
    (route: RouteOption) => {
      posthog.capture("route_option_selected", {
        route_id: route.id,
        route_title: route.title,
        option_index: routes.findIndex((r) => r.id === route.id),
        option_count: routes.length,
        total_time: route.totalTime,
        total_price: route.totalPrice,
        transfers: route.transfers,
      });
      setSelectedRoute(route);
      renderRoute(route);
      setCardView("details");
    },
    [renderRoute, posthog, routes],
  );

  const handleBackToSearch = useCallback(() => {
    setCardView("search");
    setRoutes([]);
    setRouteSearchError(null);
    setSelectedRoute(null);
    setSelectedStation(null);
    setSelectedStationDetails(null);
    setSelectedStationPin(null);
    renderRoute(null);
  }, [renderRoute, setSelectedStationPin]);

  const handleBackToOptions = useCallback(() => {
    setCardView("options");
    setSelectedRoute(null);
  }, []);

  const handleSwap = useCallback(() => {
    const newFromInput = toInput;
    const newFromLocation = toLocation;
    const newToInput = fromInput;
    const newToLocation = fromLocation;

    setFromInput(newFromInput);
    setToInput(newToInput);
    setFromLocation(newFromLocation);
    setToLocation(newToLocation);
    setIsFromGps(false);
    setFromSearchResults([]);
    setToSearchResults([]);

    if (newFromLocation) setOrigin(newFromLocation);
    if (newToLocation) setDestination(newToLocation);

    posthog.capture("origin_destination_swapped");
  }, [
    fromInput, toInput, fromLocation, toLocation,
    setOrigin, setDestination, posthog,
  ]);

  const handleEventBack = useCallback(() => {
    setSelectedEvent(null);
    selectEvent(null);
    setCardView(previousCardView);
  }, [previousCardView, selectEvent]);

  const handleStationBack = useCallback(() => {
    stationRequestRef.current += 1;
    setSelectedStation(null);
    setSelectedStationDetails(null);
    setIsLoadingStationDetails(false);
    setSelectedStationPin(null);
    setCardView(previousCardView === "station-detail" ? "search" : previousCardView);
  }, [previousCardView, setSelectedStationPin]);

  const handleRouteToStop = useCallback(() => {
    if (!selectedStation) return;

    const location: Location = {
      latitude: selectedStation.lat,
      longitude: selectedStation.lon,
      name: selectedStation.name,
    };

    setToLocation(location);
    setToInput(selectedStation.name);
    setDestination(location);
    setSearchMode("route");

    setCardView("search");
    setSelectedStationPin(selectedStation.id);
  }, [selectedStation, setDestination, setSelectedStationPin]);

  const handleEventGetDirections = useCallback(
    (event: Event) => {
      const location: Location = {
        latitude: event.lat,
        longitude: event.lon,
        name: event.venue_name || event.title,
        address: event.address || undefined,
      };
      setToLocation(location);
      setToInput(location.name || "Event Location");
      setDestination(location);
      setSelectedEvent(null);
      selectEvent(null);
      setCardView("search");
    },
    [setDestination, selectEvent],
  );

  const handleLanguageModalChange = useCallback((open: boolean) => {
    setLanguageModalOpen(open);
    if (!open) setLanguageSearch("");
  }, []);

  const handleOpenLanguageModal = useCallback(() => {
    setLanguageModalOpen(true);
    posthog.capture("language_modal_opened");
  }, [posthog]);

  const handleSearchModeChange = useCallback(
    (mode: SearchMode) => {
      if (mode !== "route") return;
      posthog.capture("search_mode_switched", { mode });
      setSearchMode(mode);
    },
    [posthog],
  );

  const handleExploreSelectLocation = useCallback(
    (result: SearchResult) => {
      const location: Location = {
        latitude: result.latitude,
        longitude: result.longitude,
        name: result.name,
        address: result.address,
      };
      flyTo(location);
    },
    [flyTo],
  );

  const handleExploreSelectEvent = useCallback(
    (event: Event) => {
      posthog.capture("event_detail_viewed", {
        event_id: event.id,
        event_title: event.title,
        event_type: event.event_type,
        source: "explore_panel",
      });
      setSelectedStation(null);
      setSelectedStationDetails(null);
      setSelectedStationPin(null);
      setSelectedEvent(event);
      setPreviousCardView((prev) =>
        prev !== "event-detail" ? prev : "search",
      );
      setCardView("event-detail");
      selectEvent(event.id);
      flyToEvent(event);
    },
    [flyToEvent, posthog, selectEvent, setSelectedStationPin],
  );

  const filteredLanguages = useMemo(
    () =>
      languages.filter((lang) =>
        lang.name.toLowerCase().includes(languageSearch.toLowerCase()),
      ),
    [languageSearch],
  );

  const filteredCities = useMemo(() => {
    const normalizedQuery = citySearch.trim().toLowerCase();
    if (!normalizedQuery) return cities;

    return cities.filter((city) => {
      const country = city.country?.toLowerCase() ?? "";
      return (
        city.name.toLowerCase().includes(normalizedQuery) ||
        city.slug.toLowerCase().includes(normalizedQuery) ||
        country.includes(normalizedQuery)
      );
    });
  }, [cities, citySearch]);

  const handleSelectCity = useCallback(
    (city: PublicCity) => {
      setSelectedCitySlug(city.slug);
      setCityModalOpen(false);
      setCitySearch("");
      posthog.capture("city_switched", {
        city_slug: city.slug,
        city_name: city.name,
      });

      setFromLocation(null);
      setToLocation(null);
      setFromInput("");
      setToInput("");
      setRoutes([]);
      setSelectedRoute(null);
      setCardView("search");
      fromLocationSetByUser.current = false;

      if (typeof city.lat === "number" && typeof city.lon === "number") {
        flyTo(
          {
            latitude: city.lat,
            longitude: city.lon,
            name: city.name,
          },
          12,
        );
      }
    },
    [flyTo, posthog],
  );

  const panelContent = (
    <>
      {cardView === "search" ? (
        <SearchCard
          mode={searchMode}
          onModeChange={handleSearchModeChange}
          exploreProps={{
            events,
            onSelectLocation: handleExploreSelectLocation,
            onSelectEvent: handleExploreSelectEvent,
          }}
          routeProps={{
            fromInput,
            toInput,
            onFromInputChange: setFromInput,
            onToInputChange: setToInput,
            fromSearchResults,
            toSearchResults,
            isSearchingFrom,
            isSearchingTo,
            onFromSelect: handleFromSelect,
            onToSelect: handleToSelect,
            onUseCurrentLocation: requestLocation,
            fromLocation,
            toLocation,
            isLoadingRoutes,
            onFindRoutes: handleFindRoutes,
            coverageWarning,
            recentSearches,
            onSelectRecentSearch: handleSelectRecentSearch,
            isFromGps,
            onSwap: handleSwap,
            onExitGpsMode: handleExitGpsMode,
            onExpandRequest: () => setSheetState("expanded"),
            onCollapseRequest: () => setSheetState("peek"),
          }}
        />
      ) : (
        <>
          <div
            className={`transition-all duration-300 ease-in-out ${cardView === "options" ? "opacity-100" : "opacity-0 hidden"
              }`}
          >
            {cardView === "options" && (
              <RouteOptionsPanel
                routes={routes}
                origin={fromLocation}
                destination={toLocation}
                isLoading={isLoadingRoutes}
                onSelectRoute={handleRouteOptionSelect}
                loadError={routeSearchError}
                onRetry={handleFindRoutes}
                onMissingReport={handleReportMissingRoute}
                recentSuggestions={recentSearches}
                onSelectSuggestion={handleSelectRecentSearch}
                onBack={handleBackToSearch}
              />
            )}
          </div>

          <div
            className={`transition-all duration-300 ease-in-out ${cardView === "details" ? "opacity-100" : "opacity-0 hidden"
              }`}
          >
            {cardView === "details" && selectedRoute && (
              <RouteDetailsPanel
                route={selectedRoute}
                onBack={handleBackToOptions}
                saved={routeSaved}
                onToggleSave={handleToggleSaveRoute}
              />
            )}
          </div>

          <div
            className={`transition-all duration-300 ease-in-out ${cardView === "event-detail" ? "opacity-100" : "opacity-0 hidden"
              }`}
          >
            {cardView === "event-detail" && selectedEvent && (
              <EventDetailPanel
                event={selectedEvent}
                onBack={handleEventBack}
                onGetDirections={handleEventGetDirections}
              />
            )}
          </div>

          <div
            className={`transition-all duration-300 ease-in-out ${cardView === "station-detail" ? "opacity-100" : "opacity-0 hidden"
              }`}
          >
            {cardView === "station-detail" && selectedStation && (
              <StopInfoPanel
                station={selectedStation}
                details={selectedStationDetails}
                isLoading={isLoadingStationDetails}
                onBack={handleStationBack}
                onRouteToStop={handleRouteToStop}
              />
            )}
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="relative w-screen h-screen overflow-hidden">
      <div className="absolute top-4 left-4 z-20 md:bottom-10 md:top-auto">
        <CityBadge
          name={cities.find((c) => c.slug === selectedCitySlug)?.name.toUpperCase() ?? "ACCRA"}
        />
      </div>
      <div className="absolute inset-0 w-full h-full bg-[#0c1524]">
        <Suspense fallback={null}>
        <Map style={resolveMapStyle(mapStyle)} center={ACCRA_CENTER} zoom={12} onCreate={setMapInstance}>
          {visibleStations.map((station) => (
            <MapMarker
              key={station.id}
              coordinates={[station.lon, station.lat]}
              anchor="bottom"
              onClick={() => {
                selectEvent(null);
                handleStationMarkerClick(station);
              }}
            >
              <MarkerContent>
                <StationPin
                  station={station}
                  isSelected={station.id === selectedStationId}
                  showLabel={stationVisibility.get(station.id)?.showLabel ?? true}
                  isVisible={true}
                />
              </MarkerContent>
            </MapMarker>
          ))}
          {events.map((event) => (
            <MapMarker
              key={event.id}
              coordinates={[event.lon, event.lat]}
              anchor={compactMode ? "center" : "bottom"}
              onClick={() => {
                selectEvent(event.id);
                handleEventMarkerClick(event);
              }}
            >
              <MarkerContent>
                <EventPin
                  event={event}
                  isSelected={event.id === selectedEventId}
                  isCompact={compactMode}
                />
              </MarkerContent>
            </MapMarker>
          ))}
          {selectedRoute && (
            <MapRoute
              key={selectedRoute.id}
              id={`route-${selectedRoute.id}`}
              steps={selectedRoute.steps as any}
              animated
              label={selectedRoute.title}
            />
          )}
        </Map>
        </Suspense>
      </div>


      <div className="hidden md:block absolute top-4 left-4 z-10 w-80">
        {cardView === "search" ? (
          panelContent
        ) : (
          <div className="rounded-2xl text-white bg-[#0009] backdrop-blur-[50px] shadow-xl shadow-black/8 overflow-hidden">
            <div className="p-3">{panelContent}</div>
          </div>
        )}
      </div>

      <BottomSheet peekHeight={340} state={sheetState} onStateChange={setSheetState}>
        {panelContent}

        <div className="mt-6 px-3 pb-6">
          <div className="flex items-center justify-center gap-3 text-sm text-white/60">
            <button
              type="button"
              onClick={handleOpenLanguageModal}
              className="hover:text-white transition-colors"
            >
              Language
            </button>
            <span aria-hidden="true">·</span>
            <a
              href="https://x.com/trotroways"
              target="_blank"
              rel="noreferrer"
              className="hover:text-white transition-colors"
            >
              About
            </a>
            <span aria-hidden="true">·</span>
            <a
              href="/privacy-policy"
              className="hover:text-white transition-colors"
            >
              Privacy
            </a>
          </div>
          <div className="text-center text-[11px] text-white/30 mt-3">
            &copy; Trotroways 2026
          </div>
        </div>
      </BottomSheet>

      <MapControls
        onRecenter={handleRecenter}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        mapStyle={mapStyle}
        onMapStyleChange={setMapStyle}
        locationDenied={locationDenied}
      />

      <div className="md:hidden absolute right-2 top-28 z-10">
        <span className="block text-[11px] text-white/40 whitespace-nowrap [writing-mode:vertical-rl]">
          © OpenFreeMap · Esri
        </span>
      </div>

      <div className="hidden md:block absolute bottom-0 left-0 right-0 z-10 pointer-events-none">
        <div className="pt-24 pb-2">
          <div className="mx-auto px-18 pointer-events-auto">
            <div className="flex items-center justify-end mb-2">
              <div className="backdrop-blur-2xl flex items-center gap-2 text-sm text-white">
                <span className="text-white/50">© OpenFreeMap · Esri</span>
                <span className="text-white">|</span>
                <Button
                  type="button"
                  variant="link"

                  onClick={handleOpenLanguageModal}
                  className="text-white transition-colors p-0 h-auto"
                >
                  Language
                </Button>
                <span className="text-white">|</span>
                <a
                  href="https://x.com/trotroways"
                  target="_blank"
                  rel="noreferrer"
                  className="text-white hover:underline "
                >
                  About
                </a>
                <span className="text-white">|</span>
                <a
                  href="/privacy-policy"
                  className="text-white hover:underline "
                >
                  Privacy
                </a></div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={languageModalOpen} onOpenChange={handleLanguageModalChange}>
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>Select Language</DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={languageSearch}
                onChange={(e) => setLanguageSearch(e.target.value)}
                placeholder="Search languages..."
                className="pl-10"
              />
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {filteredLanguages.map((lang) => (
                <Button
                  key={lang.code}
                  variant="ghost"
                  className="w-full justify-start text-left"
                  onClick={() => {
                    setLanguageModalOpen(false);
                    setLanguageSearch("");
                  }}
                >
                  <span className="text-2xl mr-3">{lang.flag}</span>
                  <span>{lang.name}</span>
                </Button>
              ))}
              {filteredLanguages.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No languages found
                </p>
              )}
            </div>
          </div>
        </DialogPopup>
      </Dialog>

      <Dialog
        open={cityModalOpen}
        onOpenChange={(open) => {
          setCityModalOpen(open);
          if (!open) setCitySearch("");
        }}
      >
        <DialogPopup>
          <DialogHeader>
            <DialogTitle>Select City</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 px-6 pb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={citySearch}
                onChange={(e) => setCitySearch(e.target.value)}
                placeholder="Search cities..."
                className="pl-10"
              />
            </div>
            <div className="max-h-72 space-y-1 overflow-y-auto">
              {filteredCities.map((city) => (
                <Button
                  key={city.id}
                  variant="ghost"
                  className="h-auto w-full justify-start gap-3 px-3 py-2.5 text-left"
                  onClick={() => handleSelectCity(city)}
                >
                  <span className="text-xl leading-none">{countryToFlag(city.country)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{city.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{city.slug}</span>
                  </span>
                </Button>
              ))}
              {filteredCities.length === 0 && (
                <p className="py-4 text-center text-sm text-muted-foreground">No cities found</p>
              )}
            </div>
          </div>
        </DialogPopup>
      </Dialog>
    </div>
  );
}
